/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useSyncExternalStore } from 'react';
import { Heart } from 'lucide-react';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/auth';
import { useTranslation } from 'react-i18next';
import { isWishlistIconVisible, subscribeWishlistIconVisibility } from '@/lib/wishlist/header-icon-visibility';

const getServerSnapshot = () => false;

/**
 * Header heart link. Hidden by default; appears once the shopper adds an item with a
 * product heart (see `markWishlistIconVisible`). Server render is always hidden, so a
 * returning shopper sees the icon right after hydration.
 */
export default function WishlistIcon(): ReactElement | null {
    const session = useAuth();
    const { t } = useTranslation('header');
    const isVisible = useSyncExternalStore(subscribeWishlistIconVisibility, isWishlistIconVisible, getServerSnapshot);
    const isAuthenticated = session?.userType === 'registered' && Boolean(session?.customerId);
    const wishlistLink = isAuthenticated ? '/account/wishlist' : '/wishlist';

    if (!isVisible) {
        return null;
    }

    return (
        <Button
            variant="ghost"
            className="cursor-pointer hover:bg-transparent hover:opacity-50 transition-opacity flex-col h-auto gap-0.5 px-2 py-1 lg:flex-row lg:h-9 lg:gap-2 lg:px-5 lg:py-2"
            asChild>
            <Link to={wishlistLink} aria-label={t('wishlist')} data-testid="header-wishlist-icon">
                <Heart className="size-5" />
                <span className="text-xs font-medium" aria-hidden="true">
                    {t('wishlist')}
                </span>
            </Link>
        </Button>
    );
}
